import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Plus, Repeat } from 'lucide-react'

import { PageContainer } from '@/components/common/page-container'
import { PageHeader } from '@/components/common/page-header'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { LoopCard } from '@/features/loops/loop-card'
import { LoopFormDialog } from '@/features/loops/loop-form-dialog'
import { loopsListQuery, type Loop } from '@/api/loops'
import { RouteError } from '@/components/common/route-error'

/**
 * /loops — list page (mockup 05).
 *
 * Loader prefetches the first page so the cards land on first render.
 * Create + Edit share the same dialog component, differentiated by the
 * `editing` state.
 */
export const Route = createFileRoute('/_authed/loops')({
  loader: ({ context }) => context.queryClient.ensureQueryData(loopsListQuery(1, 50)),
  component: LoopsPage,
  errorComponent: RouteError,
})

function LoopsPage() {
  const { data } = useSuspenseQuery(loopsListQuery(1, 50))
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Loop | undefined>(undefined)

  const openCreate = () => {
    setEditing(undefined)
    setDialogOpen(true)
  }
  const openEdit = (loop: Loop) => {
    setEditing(loop)
    setDialogOpen(true)
  }

  return (
    <PageContainer>
      <PageHeader
        title="Job Loops"
        description="Persistent searches that scrape and match new jobs every few hours."
        actions={
          <Button onClick={openCreate}>
            <Plus />
            New loop
          </Button>
        }
      />

      {data.items.length === 0 ? (
        <EmptyState
          icon={Repeat}
          title="No loops yet"
          description="Create your first loop to start finding matching jobs."
          action={
            <Button onClick={openCreate}>
              <Plus />
              Create a loop
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.items.map((loop) => (
            <LoopCard key={loop.id} loop={loop} onEdit={openEdit} />
          ))}
        </div>
      )}

      <LoopFormDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} />
    </PageContainer>
  )
}
